import etuBg from './etu3.jpg';
import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../Firebase';
import { Send, Users, User, Bell, Search } from 'lucide-react';

export default function AdminCommunication({ staff }) {
  const [etudiants, setEtudiants] = useState([]);
  const [messages, setMessages]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [form, setForm]           = useState({ destinataire: 'tous', etudiantId: '', sujet: '', message: '', type: 'info' });
  const [sending, setSending]     = useState(false);
  const [msg, setMsg]             = useState('');
  const [search, setSearch]       = useState('');

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    const etSnap = await getDocs(collection(db, 'etudiants'));
    setEtudiants(etSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(e => !e.role || e.role === 'etudiant'));
    const msgSnap = await getDocs(collection(db, 'messages'));
    const msgs = msgSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    msgs.sort((a,b) => (b.createdAt?.seconds||0) - (a.createdAt?.seconds||0));
    setMessages(msgs);
    setLoading(false);
  }

  async function handleSend() {
    if (!form.sujet || !form.message) return;
    setSending(true);
    const id = `msg_${Date.now()}`;
    const dest = form.destinataire === 'tous'
      ? { type: 'tous', label: 'Tous les étudiants' }
      : { type: 'individuel', etudiantId: form.etudiantId, label: etudiants.find(e => e.id === form.etudiantId)?.nom || '' };
    await setDoc(doc(db, 'messages', id), {
      ...dest,
      sujet: form.sujet,
      message: form.message,
      type: form.type,
      envoyePar: staff?.nom || 'Admin',
      envoyeParRole: staff?.role,
      createdAt: serverTimestamp(),
      lu: false,
    });
    setMsg('Message envoyé avec succès.');
    setForm({ destinataire: 'tous', etudiantId: '', sujet: '', message: '', type: 'info' });
    await fetchData();
    setSending(false);
    setTimeout(() => setMsg(''), 3000);
  }

  const filtered = messages.filter(m =>
    m.sujet?.toLowerCase().includes(search.toLowerCase()) ||
    m.label?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="admin-page-loading"><div className="admin-spinner"/></div>;

  return (
    <div className="admin-page admin-page-bg--communication"><div style={{backgroundImage:`url(${etuBg})`,backgroundSize:"cover",backgroundPosition:"center",position:"fixed",inset:0,opacity:0.15,pointerEvents:"none",zIndex:0}}/>
      <div className="admin-page__header">
        <div>
          <h2>Communication</h2>
          <p>Envoyez des messages aux étudiants</p>
        </div>
      </div>

      {msg && <div className="admin-msg admin-msg--ok">{msg}</div>}

      {/* Formulaire d'envoi */}
      <div className="admin-form-box">
        <h3><Send size={16}/> Nouveau message</h3>
        <div className="admin-form-grid">
          <div className="admin-form-field">
            <label>Destinataire</label>
            <select value={form.destinataire} onChange={e => setForm(p => ({...p, destinataire: e.target.value}))}>
              <option value="tous">Tous les étudiants</option>
              <option value="individuel">Un étudiant spécifique</option>
            </select>
          </div>
          {form.destinataire === 'individuel' && (
            <div className="admin-form-field">
              <label>Étudiant</label>
              <select value={form.etudiantId} onChange={e => setForm(p => ({...p, etudiantId: e.target.value}))}>
                <option value="">Sélectionner...</option>
                {etudiants.map(e => <option key={e.id} value={e.id}>{e.nom}</option>)}
              </select>
            </div>
          )}
          <div className="admin-form-field">
            <label>Type</label>
            <select value={form.type} onChange={e => setForm(p => ({...p, type: e.target.value}))}>
              <option value="info">Information</option>
              <option value="rappel">Rappel</option>
              <option value="alerte">Alerte</option>
              <option value="felicitation">Félicitation</option>
            </select>
          </div>
          <div className="admin-form-field" style={{gridColumn:'1/-1'}}>
            <label>Sujet</label>
            <input type="text" value={form.sujet} onChange={e => setForm(p => ({...p, sujet: e.target.value}))} placeholder="Objet du message"/>
          </div>
          <div className="admin-form-field" style={{gridColumn:'1/-1'}}>
            <label>Message</label>
            <textarea rows={5} value={form.message} onChange={e => setForm(p => ({...p, message: e.target.value}))} placeholder="Rédigez votre message..."/>
          </div>
        </div>
        <button className="admin-btn admin-btn--primary" onClick={handleSend} disabled={sending} style={{marginTop:'1rem'}}>
          {sending ? <span className="admin-spinner-sm"/> : <><Send size={15}/> Envoyer</>}
        </button>
      </div>

      {/* Historique */}
      <div className="admin-table-box" style={{marginTop:'1.5rem'}}>
        <div className="admin-table-box__header">
          <h3>Historique des messages</h3>
          <div className="admin-search" style={{width:'260px'}}>
            <Search size={14}/>
            <input type="text" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)}/>
          </div>
        </div>
        <table className="admin-table">
          <thead>
            <tr><th>Destinataire</th><th>Sujet</th><th>Type</th><th>Envoyé par</th><th>Date</th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={5} style={{textAlign:'center',padding:'2rem',color:'var(--admin-muted)'}}>Aucun message</td></tr>}
            {filtered.map(m => (
              <tr key={m.id}>
                <td>
                  <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                    {m.type === 'tous' ? <Users size={14}/> : <User size={14}/>}
                    <span>{m.label || 'Tous'}</span>
                  </div>
                </td>
                <td>{m.sujet}</td>
                <td><span className={`admin-badge admin-badge--${m.type==='alerte'?'err':m.type==='felicitation'?'ok':'info'}`}>{m.type}</span></td>
                <td>{m.envoyePar}</td>
                <td>{m.createdAt ? new Date(m.createdAt.seconds*1000).toLocaleDateString('fr-FR') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}